export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export interface HttpClient {
  request<T>(url: string, method: HttpMethod, body?: Record<string, unknown>, headers?: Record<string, string>): Promise<T>;
}

export interface AuthHttpDeps {
  getBaseUrl: () => string;
  getDefaultHeaders: () => Record<string, string>;
}

export class HttpResponseError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'HttpResponseError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Auth-package HTTP client. Uses native fetch with proper status-code checking.
 * Rejects on any non-2xx response instead of silently resolving.
 */
export function createAuthHttpClient(deps: AuthHttpDeps): HttpClient {
  return {
    async request<T>(url: string, method: HttpMethod, body?: Record<string, unknown>, headers?: Record<string, string>): Promise<T> {
      const baseUrl = deps.getBaseUrl();
      const mergedHeaders: Record<string, string> = {
        ...deps.getDefaultHeaders(),
        ...headers,
      };

      const options: RequestInit = { method, headers: mergedHeaders };
      if (body && method !== 'GET') {
        options.body = JSON.stringify(body);
      }

      const response = await fetch(`${baseUrl}${url}`, options);

      if (response.status === 204) {
        return {} as T;
      }

      let data: unknown;
      try {
        data = await response.json();
      } catch {
        if (!response.ok) {
          throw new HttpResponseError(response.status, `Request failed with status ${response.status}`);
        }
        return {} as T;
      }

      if (!response.ok) {
        const extracted = extractErrorInfo(data);
        const message = extracted.message ?? `Request failed with status ${response.status}`;
        throw new HttpResponseError(response.status, message, extracted.code);
      }

      return data as T;
    },
  };
}

function extractErrorInfo(data: unknown): { message?: string; code?: string } {
  if (!data || typeof data !== 'object') return {};
  const obj = data as Record<string, unknown>;

  const code = typeof obj.error === 'string' ? obj.error : undefined;
  const message = typeof obj.message === 'string' ? obj.message : code;

  if (message) return { message, code };

  if (Array.isArray(obj.errors) && obj.errors.length > 0) {
    const first = obj.errors[0];
    if (typeof first === 'object' && first !== null && typeof (first as Record<string, unknown>).title === 'string') {
      return { message: (first as Record<string, unknown>).title as string };
    }
  }

  return {};
}
